import React, { useEffect, useState } from "react";
import * as actions from "../Common/actions";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Box, Paper, TextField, Typography, Divider, Chip, InputAdornment } from "@mui/material";
import CustomAddModal from "../Common/CustomAddModal";
import { toastSuccess, toastError, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession } from "../../utils";
import moment from "moment";
import UserDetailsPasswordModal from './UserDetailsPasswordModal';
import CopyableField from '../../components/CopyableField';
import ActionButton from '../../components/ActionButton';
import StatCard from '../../components/StatCard';
import { User, Mail, Phone, MapPin, Calendar, Shield, DollarSign, Disc3, FileText, Users, Ticket, Landmark, Clock } from 'lucide-react';

// Helper function to calculate time ago with detailed breakdown
const getTimeAgo = (dateString) => {
  if (!dateString) return "-";

  const updatedDate = new Date(dateString);
  const now = new Date();

  let years = now.getFullYear() - updatedDate.getFullYear();
  let months = now.getMonth() - updatedDate.getMonth();
  let days = now.getDate() - updatedDate.getDate();

  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

  if (parts.length === 0) return "Today";
  return parts.join(', ');
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function ViewUserDetails(props) {
  const params = useParams();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState({});
  const [bankDetails, setBankDetails] = useState({});
  const [openRevenue, setOpenRevenue] = useState(false);
  const [addRevenue, setAddRevenue] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const sessionValid = checkUserDetailsSession();
    if (sessionValid) {
      setIsAuthenticated(true);
      getUserDetailsById();
    } else {
      setShowPasswordModal(true);
      clearUserDetailsSession();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const intervalId = setInterval(() => {
      const sessionValid = checkUserDetailsSession();
      if (!sessionValid) {
        setIsAuthenticated(false);
        setShowPasswordModal(true);
        setPasswordError('Your session has expired. Please re-enter the password.');
      }
    }, 300000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const sessionValid = checkUserDetailsSession();
        if (!sessionValid) {
          setIsAuthenticated(false);
          setShowPasswordModal(true);
          setPasswordError('Your session has expired. Please re-enter the password.');
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const getUserDetailsById = () => {
    props.setLoader(true);
    actions
      .getUserDetailsByIdForAdmin(params.id)
      .then((res) => {
        props.setLoader(false);
        setUserDetails(res.userDetails);
        setBankDetails(res.bankDetails);
        setAddRevenue(res.userDetails.revenue);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load user details');
        navigate("/user_details");
      });
  };

  const handlePasswordVerification = (password) => {
    setPasswordLoading(true);
    setPasswordError('');
    actions.verifyUserDetailsPassword({ password })
      .then((res) => {
        setPasswordLoading(false);
        setShowPasswordModal(false);
        setIsAuthenticated(true);
        setUserDetailsSession(Date.now());
        getUserDetailsById();
        toastSuccess('Access granted');
      })
      .catch((err) => {
        setPasswordLoading(false);
        setPasswordError(err?.message || 'Invalid password. Please try again.');
      });
  };

  const onUpdateRevenue = () => {
    props.setLoader(true);
    actions
      .addRevenueAdmin({ user_id: params.id, revenue: addRevenue })
      .then((res) => {
        setOpenRevenue(false);
        getUserDetailsById();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to add revenue');
      });
  };

  const {
    name = "", email = "", phone = "", address = "", created_at = "",
    ip = "", revenue = "", revoked = "", total_releases = "",
    total_drafts = "", total_artist = "", total_reports_requested = "",
    total_tickets_created = "", total_open_tickets = "",
  } = userDetails;

  const {
    bank_name = "", ifsc_code = "", account_number = "", holder_name = "",
    branch_name = "", pan = "", other_details = "", updated_on = "",
  } = bankDetails;

  if (!isAuthenticated) {
    return (
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
    );
  }

  return (
    <>
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />

      <Grid container className="view-release-container" spacing={2}>
        {/* ==================== HEADER ==================== */}
        <Grid item xs={12}>
          <Box className="view-release-header">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                {name || 'User Details'}
              </Typography>
              {email && (
                <Chip
                  size="small"
                  label={email}
                  icon={<Mail size={13} />}
                  sx={{
                    backgroundColor: 'rgba(108, 99, 255, 0.15)',
                    color: '#6C63FF',
                    fontWeight: 500,
                    fontSize: '0.78rem',
                    flexShrink: 0,
                    '& .MuiChip-icon': { color: '#6C63FF' },
                  }}
                />
              )}
              {revoked === 1 && (
                <Chip
                  size="small"
                  label="Revoked"
                  sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    color: '#EF4444',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                  }}
                />
              )}
            </Box>
            <ActionButton variant="settle" onClick={() => navigate('/user_details')}>
              Back to Users
            </ActionButton>
          </Box>
        </Grid>

        {/* ==================== LEFT SIDEBAR ==================== */}
        <Grid item xs={12} md={3}>
          <Box className="view-release-sidebar">
            {/* User Avatar Area */}
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '24px 16px', backgroundColor: 'var(--bg-elevated)',
              borderRadius: '8px', border: '1px solid var(--border)',
            }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                backgroundColor: 'rgba(108, 99, 255, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
              }}>
                <User size={32} color="#6C63FF" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>
                {name || '-'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'center' }}>
                Joined {moment(created_at).format("DD MMM YYYY")}
              </Typography>
            </Box>

            {/* Quick Contact Info */}
            <Box mt={2} className="sidebar-user-info">
              <CopyableField label="Email" value={email} />
              <CopyableField label="Phone" value={phone} />
              <CopyableField label="IP" value={ip} />
            </Box>

            {/* Actions */}
            <Box mt={2} className="sidebar-actions">
              <Typography variant="subtitle2" className="sidebar-actions__title">Actions</Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton variant="success" onClick={() => setOpenRevenue(true)}>
                  Add Revenue
                </ActionButton>
              </div>
            </Box>
          </Box>
        </Grid>

        {/* ==================== RIGHT CONTENT ==================== */}
        <Grid item xs={12} md={9}>
          <Box className="view-release-content">

            {/* Stats Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 2 }}>
              <StatCard title="Revenue" value={`$${revenue || 0}`} icon={DollarSign} color="#10B981" />
              <StatCard title="Releases" value={total_releases || 0} icon={Disc3} color="#6C63FF" />
              <StatCard title="Drafts" value={total_drafts || 0} icon={FileText} color="#F59E0B" />
              <StatCard title="Artists" value={total_artist || 0} icon={Users} color="#3B82F6" />
              <StatCard title="Open Tickets" value={total_open_tickets || 0} icon={Ticket} color="#EF4444" />
            </Box>

            {/* Section: User Information */}
            <Paper className="section-card">
              <Typography variant="h6" className="section-card__title">User Information</Typography>
              <Divider sx={{ mb: 2, borderColor: 'var(--border)' }} />
              <CopyableField label="Name" value={name} />
              <CopyableField label="Email" value={email} />
              <CopyableField label="Phone" value={phone} />
              <CopyableField label="Address" value={address} />
              <CopyableField label="Signup Date" value={moment(created_at).format("DD MMM YYYY, hh:mm A")} />
              <CopyableField label="IP Address" value={ip} />
              <CopyableField label="Revoked" value={revoked === 0 ? "No" : "Yes"} highlight={revoked === 1 ? "#EF4444" : undefined} />
              <CopyableField label="Total Releases" value={total_releases} />
              <CopyableField label="Total Drafts" value={total_drafts} />
              <CopyableField label="Total Artists" value={total_artist} />
              <CopyableField label="Reports Requested" value={total_reports_requested} />
              <CopyableField label="Tickets Created" value={total_tickets_created} />
              <CopyableField label="Open Tickets" value={total_open_tickets} highlight={total_open_tickets > 0 ? "#F59E0B" : undefined} />
            </Paper>

            {/* Section: Bank Details */}
            <Paper className="section-card">
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" className="section-card__title" sx={{ mb: '0 !important' }}>Bank Details</Typography>
                {updated_on && (
                  <Chip
                    size="small"
                    icon={<Clock size={12} />}
                    label={`Updated ${getTimeAgo(updated_on)} ago`}
                    sx={{
                      backgroundColor: 'rgba(239, 68, 68, 0.12)',
                      color: '#EF4444',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      '& .MuiChip-icon': { color: '#EF4444' },
                    }}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'var(--border)' }} />
              <CopyableField label="Bank Name" value={bank_name} />
              <CopyableField label="IFSC Code" value={ifsc_code} />
              <CopyableField label="Account Number" value={account_number} highlight="#F59E0B" />
              <CopyableField label="Holder Name" value={holder_name} />
              <CopyableField label="Branch Name" value={branch_name} />
              <CopyableField label="PAN Number" value={pan} highlight="#EF4444" />
              <CopyableField label="Other Details" value={other_details} />
              <CopyableField label="Updated On" value={formatDate(updated_on)} />
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Add Revenue Modal */}
      <CustomAddModal
        open={openRevenue}
        title="Add/Update Revenue"
        handleClose={() => setOpenRevenue(false)}
        onSubmitConfirm={onUpdateRevenue}
        saveLabel="Update"
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1.5, fontSize: '0.85rem' }}>
            Current revenue: <span style={{ color: '#10B981', fontWeight: 600 }}>${revenue || 0}</span>
          </Typography>
          <TextField
            type="number"
            variant="outlined"
            fullWidth
            value={addRevenue}
            onChange={(e) => setAddRevenue(e.target.value)}
            placeholder="Enter revenue amount"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DollarSign size={16} color="#94A3B8" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </CustomAddModal>
    </>
  );
}

export default ViewUserDetails;
